import React, { useEffect, useState } from "react";
import { Icon } from '@iconify/react';
import { getDailyAISummary } from '../../api/ai-summaryAPI';
import { getWorkOrderById } from '../../api/work-orderAPI';

const AIDailySummaryCard  = () => {
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setError] = useState('');

  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleIconClick = async (url) => {
    const match = url.match(/\/work-orders\/(\d+)/);
    if (match) {
      const id = parseInt(match[1], 10);
      try {
        const wo = await getWorkOrderById(id);
        setSelectedWorkOrder(wo);
        setShowModal(true);
      } catch (err) {
        console.error('Failed to load work order:', err);
      }
    }
  };

  const renderLine = (line, index) => {
    // Match both numeric and alphanumeric work order links, e.g. [#AAA-123](...)
    const workOrderRegex = /\[#([^\]]+)\]\(([^)]+)\)/g;
    const boldInlineRegex = /\*\*(.*?)\*\*/g;
    const boldStartMatch = line.match(/^\s*-\s+\*\*(.*?)\*\*(.*)$/);
    const bulletMatch = line.match(/^(\s*)-\s+(.*)$/); // captures indent and content

    const processText = (text) => {
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = workOrderRegex.exec(text)) !== null) {
        const [, displayText, url] = match;
        const matchStart = match.index;
        const matchEnd = workOrderRegex.lastIndex;

        if (matchStart > lastIndex) {
          parts.push(text.slice(lastIndex, matchStart));
        }

        parts.push(
          <span
            key={`wo-${displayText}-${index}-${matchStart}`}
            onClick={() => handleIconClick(url)}
            style={{
              cursor: 'pointer',
              color: '#0d6efd',
              textDecoration: 'underline',
              marginRight: '6px',
            }}
          >
            [#{displayText}]
          </span>
        );

        lastIndex = matchEnd;
      }

      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }

      // Apply **bold** formatting inside remaining parts
      return parts.flatMap((part, i) => {
        if (typeof part !== 'string') return part;
        const boldChunks = part.split(boldInlineRegex);
        return boldChunks.map((chunk, j) =>
          j % 2 === 1 ? (
            <strong key={`${index}-${i}-${j}`}>{chunk}</strong>
          ) : (
            <React.Fragment key={`${index}-${i}-${j}`}>{chunk}</React.Fragment>
          )
        );
      });
    };

    // Case 1: Starts with - **bold** (but not bulleted)
    if (boldStartMatch) {
      const [, boldText, rest] = boldStartMatch;
      return (
        <div key={index} style={{ whiteSpace: 'pre-wrap' }}>
          <strong>{boldText}</strong>{rest}
        </div>
      );
    }

    // Case 2: Bulleted lines (may be indented)
    if (bulletMatch) {
      const indent = bulletMatch[1].length;
      const rawText = bulletMatch[2];
      const margin = indent >= 6 ? '3rem' : indent >= 3 ? '2rem' : undefined;

      return (
        <li key={index} style={margin ? { marginLeft: margin } : undefined}>
          {processText(rawText)}
        </li>
      );
    }

    // Case 3: Normal paragraph
    return (
      <div key={index} style={{ whiteSpace: 'pre-wrap' }}>
        {processText(line)}
      </div>
    );
  };

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await getDailyAISummary();
        setSummary(result.summary);
      } catch (err) {
        setError('Failed to load AI summary.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  return (
    <>
      <div className='col-xxl-8' style={{ height: "426px" }}>
        <div className='card h-100'>
          <div className='card-body p-24 mb-8' style={{ overflow: "auto" }}>
            <h6 className="mb-4 fw-bold text-lg">Daily Summary</h6>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                  <Icon icon="line-md:loading-loop" width="32" height="32" />
                </div>
              ) : (
                <div>{summary.split('\n').map(renderLine)}</div>
              )}
          </div>
        </div>
      </div>
      {showModal && selectedWorkOrder && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Work Order #{selectedWorkOrder.id}</h6>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <p><strong className="text-lg"> {selectedWorkOrder.workOrderTitle}</strong></p>
                <p><strong></strong> {selectedWorkOrder.workOrderDescription}</p>
                <p><strong>Status:</strong> {selectedWorkOrder.status?.name}</p>
                <p><strong>Priority:</strong> {selectedWorkOrder.priority?.name}</p>
                <p><strong>Asset:</strong> {selectedWorkOrder.asset?.assetNumber + ' - ' + selectedWorkOrder.asset?.assetDescription}</p>
                <p><strong>Assigned Team:</strong> {selectedWorkOrder.team?.teamName}</p>
                <p><strong>Scheduled Start:</strong> {selectedWorkOrder.scheduledAt}</p>
                <p><strong>Due Date:</strong> {selectedWorkOrder.dueDate}</p>
                {/* Add more fields if needed */}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
  );
};

export default AIDailySummaryCard ;
